/* PHOTOSHOP-STYLE OPACITY FOR A BLEND MODE.

   scene/objects.js's applyBlendMode sets a material's blend equation to
   something exact — Multiply IS src*dst, Screen IS src+dst-src*dst — but a
   single glBlendFunc call has no slot for "and then mix that result back
   toward the original by this much". Only Normal and Add read source alpha
   at all; Multiply/Screen/Subtract/Darken/Lighten's correct factors don't
   reference it, so opacity does nothing under them — confirmed against
   three.js's own WebGLState, not assumed. Photoshop's opacity+blend-mode is
   a SECOND composite: apply the mode at full strength, then normal-blend
   THAT result over the original at the chosen opacity. One glBlendFunc call
   cannot do both, so this does it in two real passes:

     1. Render everything except the blended objects — the backdrop.
     2. For each blended object, in turn: seed the framebuffer with the
        composite so far, render JUST that object into it (its own blend
        equation composites against the seed), read the result back, and
        alpha-composite it over the running total at its own opacity — this
        step is what makeS opacity work, and it is exactly the formula
        Photoshop uses: final = backdrop·(1-opacity) + blended·opacity.
     3. Push the finished composite back into the framebuffer.

   The "render into a texture, read it back" step goes through a plain 2D
   canvas rather than a WebGLRenderTarget — the renderer already runs with
   preserveDrawingBuffer:true (scene/context.js) specifically so its own
   canvas can be read with drawImage, which export/frame.js's downscale()
   already relies on for the exact same reason. Reusing that instead of
   introducing render-target infrastructure keeps this consistent with how
   the rest of the app already gets pixels out of the renderer.

   KNOWN LIMITATION, not fixed here: the isolated per-object pass renders
   against a freshly seeded (fully cleared) depth buffer, so an opaque
   object positioned IN FRONT of a blended one — which is hidden during that
   pass along with everything else — will not occlude it. Fixing that needs
   the backdrop's depth carried through too, which means a real
   WebGLRenderTarget with a depth texture and a shader to seed both buffers
   at once — a materially bigger change than this. The common case this
   serves — an image blending against whatever is directly behind it — is
   unaffected; only "something nearer hides it" is not, and image objects
   with a blend mode are the only thing that can hit this today. */
import * as THREE from 'three';
import { renderer, scene } from './context.js';
import { S, clamp } from '../state/store.js';
import { applyAO } from './ao.js';
import { applyDOF } from './dof.js';

const quadScene = new THREE.Scene();
const quadCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
const quadCanvas = document.createElement('canvas');
const quadTex = new THREE.CanvasTexture(quadCanvas);
quadTex.colorSpace = THREE.SRGBColorSpace;   // matches renderer.outputColorSpace — no gamma shift on re-upload
const quadMat = new THREE.MeshBasicMaterial({ map: quadTex, depthTest: false, depthWrite: false, toneMapped: false });
quadScene.add(new THREE.Mesh(new THREE.PlaneGeometry(2, 2), quadMat));

/** Push a 2D canvas's current pixels into whatever the renderer is
 *  currently targeting, replacing its contents. */
function blit(canvas2d){
  quadTex.image = canvas2d;
  quadTex.needsUpdate = true;
  renderer.render(quadScene, quadCam);
}

/** Copy the renderer's current drawing buffer into a reused 2D canvas. */
function readBack(into){
  const gl = renderer.getContext();
  const w = gl.drawingBufferWidth, h = gl.drawingBufferHeight;
  if(into.width !== w || into.height !== h){ into.width = w; into.height = h; }
  into.getContext('2d').drawImage(renderer.domElement, 0, 0, w, h, 0, 0, w, h);
  return into;
}

const isBlended = o => o.visible && o.blend && o.blend !== 'normal' && o.opacity < 1 && o.node;

const _backdrop = document.createElement('canvas');
const _layer = document.createElement('canvas');
const _wa = new THREE.Vector3(), _wb = new THREE.Vector3();

/**
 * Drop-in replacement for `renderer.render(sceneObj, cam)` — same inputs,
 * same postcondition (the finished frame sits in the renderer's current
 * target), correct opacity for every blend mode. Falls through to a plain
 * render with no extra cost when nothing needs it.
 */
export function renderComposited(sceneObj, cam){
  const targets = S.objects.filter(isBlended);
  if(!targets.length){ renderer.render(sceneObj, cam); applyAO(cam); applyDOF(cam); return; }

  // Back-to-front, the same convention three.js's own transparency sort
  // uses — each pass below is a separate render() call, so nothing sorts
  // these against each other automatically the way one call would.
  const eye = cam.position;
  targets.sort((a, b) => {
    const da = a.node.getWorldPosition(_wa).distanceToSquared(eye);
    const db = b.node.getWorldPosition(_wb).distanceToSquared(eye);
    return db - da;
  });

  const savedAutoClear = renderer.autoClear;
  const shown = targets.map(o => o.node.visible);
  targets.forEach(o => { o.node.visible = false; });

  renderer.autoClear = savedAutoClear;
  renderer.render(sceneObj, cam);              // the backdrop, without any blended object
  applyAO(cam);                                // shaded before the blended layers go over it
  readBack(_backdrop);
  const ctx = _backdrop.getContext('2d');

  for(const o of targets){
    /* The isolated pass has to compute the mode at FULL strength — "what
       would Multiply/Screen/etc. do to this pixel" — with opacity applied
       only once, afterward, in the 2D composite below. Left at the
       material's real opacity, the blend equation's own alpha channel
       carries that same 0.5 a second time: not through the RGB blend
       factors (confirmed against three.js's own WebGLState — Multiply's
       DstColor/Zero never reference alpha), but through ALPHA itself,
       since a bare (non-separate) blendFunc applies its factors to alpha
       too, and that alpha then discounts the pixel a second time the
       moment drawImage below reads it back through it. Forced to 1 on
       every material this object owns, then put back exactly as it
       was — syncNode never runs in between, so nothing else observes it. */
    const mats = (o.node.userData.mats || []).filter(Boolean);
    const savedOpacity = mats.map(m => m.opacity);
    mats.forEach(m => { m.opacity = 1; });

    renderer.autoClear = true;
    blit(_backdrop);                            // seed with the composite so far
    o.node.visible = true;
    renderer.autoClear = false;
    renderer.render(sceneObj, cam);              // just this object, blended against the seed
    o.node.visible = false;
    readBack(_layer);

    mats.forEach((m, i) => { m.opacity = savedOpacity[i]; });

    ctx.globalAlpha = clamp(o.opacity, 0, 1);
    ctx.drawImage(_layer, 0, 0);
    ctx.globalAlpha = 1;
  }

  targets.forEach((o, i) => { o.node.visible = shown[i]; });
  renderer.autoClear = true;
  blit(_backdrop);                              // leave the finished composite in place
  renderer.autoClear = savedAutoClear;
  applyDOF(cam);                                // last, so the blended layers are blurred too
}
